import { credentialMarkup, loaderMarkup } from "../components";
import { appContent } from "../content";
import { adminCopy } from "../copy/admin";
import type { AdminUser, ContentReportsPage, ContentReportStatus, CurrentUser, JimReadAdmin, ReadSuggestion, SessionState, TuningRule, ZeroResultQuery } from "../types";
import { escapeHtml } from "../utils";
import { adminConsoleTabs, bindConsoleTabs, bindSubTabs, consoleShellMarkup, panelMarkup, settingsConsoleTabs, subTabMarkup, type ConsoleTab, type SettingsTab } from "./adminShell";
import { bindReadsSection, createReadMarkup, draftReadCount, needsReadMarkup, reviewQueueMarkup } from "./adminReads";
import { bindReportsSection, reportsMarkup } from "./adminReports";
import { bindSettingsForm, changeEmailMarkup, changePasswordMarkup } from "./settings";

type TemporaryCredential = NonNullable<SessionState["setupCredential"]>;
type UsersTab = "list" | "create";
type TermsTab = "general" | "clause" | "zeroes";
type ReadsTab = "create" | "queue" | "needs";
type ReportsTab = "all" | ContentReportStatus;

type AdminScreenOptions = {
  currentUser: CurrentUser | null;
  message: string;
  setupCredential: SessionState["setupCredential"];
  renderWorkspace: (content: string) => void;
  bindWorkspaceNavigation: () => void;
  bindCopyButtons: () => void;
  reloadAdmin: () => void;
  listUsers: () => Promise<{ users: AdminUser[] }>;
  createUser: (username: string, role: string) => Promise<TemporaryCredential>;
  resetPassword: (userId: string) => Promise<TemporaryCredential>;
  setLicensedAccess: (userId: string, enabled: boolean) => Promise<void>;
  disableUser: (userId: string) => Promise<void>;
  enableUser: (userId: string) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  listTuning: () => Promise<{ rules: TuningRule[]; zeroResultQueries: ZeroResultQuery[] }>;
  createTuning: (body: Record<string, unknown>) => Promise<TuningRule>;
  deleteTuning: (id: string) => Promise<void>;
  listReads: () => Promise<{ reads: JimReadAdmin[]; suggestions: ReadSuggestion[] }>;
  createRead: (clauseNumber: string, body: string) => Promise<JimReadAdmin>;
  updateRead: (id: string, body: string, updatedAt: string) => Promise<JimReadAdmin>;
  approveRead: (id: string, updatedAt: string) => Promise<JimReadAdmin>;
  deleteRead: (id: string) => Promise<void>;
  listReports: (status: ContentReportStatus | null, page: number) => Promise<ContentReportsPage>;
  updateReportStatus: (id: string, status: ContentReportStatus, adminNote: string) => Promise<void>;
  downloadStats: () => Promise<{ blob: Blob; filename: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  changeEmail: (email: string, confirmEmail: string) => Promise<{ email: string }>;
  onCredential: (credential: TemporaryCredential) => void;
  onMessage: (message: string) => void;
  onUserDisabled: (username: string) => void;
};

function confirmationMarkup(userId: string, action: "reset" | "disable" | "delete"): string {
  const copy = action === "reset"
    ? { prompt: adminCopy.table.confirmResetPassword, button: adminCopy.table.confirmReset }
    : action === "disable"
      ? { prompt: adminCopy.table.confirmDisable, button: adminCopy.table.confirmDisableAction }
      : { prompt: adminCopy.table.confirmDeletePrompt, button: adminCopy.table.confirmDelete };
  return `
    <div class="inline-confirm" data-user-id="${escapeHtml(userId)}" data-confirm-action="${action}" hidden>
      <span>${copy.prompt}</span>
      <button type="button" class="ghost danger confirmUserActionButton" data-user-id="${escapeHtml(userId)}" data-confirm-action="${action}">${copy.button}</button>
      <button type="button" class="ghost cancelUserActionButton" data-user-id="${escapeHtml(userId)}" data-confirm-action="${action}">${adminCopy.table.cancel}</button>
    </div>
  `;
}

function usersTableMarkup(users: AdminUser[], rowMessages: Map<string, string>): string {
  return `
    <div class="users-table-scroll"><table class="users-table">
      <thead><tr><th>${adminCopy.table.username}</th><th>${adminCopy.table.email}</th><th>${adminCopy.table.role}</th><th>${adminCopy.table.status}</th><th>${adminCopy.table.licensedAccess}</th><th>${adminCopy.table.created}</th><th>${adminCopy.table.actions}</th></tr></thead>
      <tbody>${users.map((user) => `
        <tr class="${user.role === "disabled" ? "user-row-disabled" : ""}">
          <td>${escapeHtml(user.username)}</td>
          <td>${user.email ? escapeHtml(user.email) : "&mdash;"}</td>
          <td>${escapeHtml(user.role.replace("_", " "))}${user.role === "disabled" && user.previousRole ? `<span class="user-previous-role">${adminCopy.table.disabledRole}: ${escapeHtml(user.previousRole.replace("_", " "))}</span>` : ""}</td>
          <td>${user.role === "disabled" ? adminCopy.table.disabled : user.mustChangePassword ? adminCopy.table.passwordChangeRequired : user.lockedUntil ? adminCopy.table.locked : adminCopy.table.active}</td>
          <td><button type="button" class="ghost licensedAccessButton" data-user-id="${escapeHtml(user.id)}" data-enabled="${String(user.canViewVerbatim)}">${adminCopy.table.licensedAccess}: ${user.canViewVerbatim ? adminCopy.table.active : adminCopy.table.disabled}</button></td>
          <td>${escapeHtml(user.createdAt)}</td>
          <td><div class="row-actions">
            <button type="button" class="ghost resetUserButton" data-user-id="${escapeHtml(user.id)}">${adminCopy.table.resetPassword}</button>
            ${user.role === "disabled" ? `<button type="button" class="ghost enableUserButton" data-user-id="${escapeHtml(user.id)}">${adminCopy.table.enable}</button>` : user.role === "super_admin" ? "" : `<button type="button" class="ghost danger disableUserButton" data-user-id="${escapeHtml(user.id)}" data-username="${escapeHtml(user.username)}">${adminCopy.table.disable}</button>`}
            ${user.role === "super_admin" ? "" : `<button type="button" class="ghost danger deleteUserButton" data-user-id="${escapeHtml(user.id)}">${adminCopy.table.delete}</button>`}
          </div>${confirmationMarkup(user.id, "reset")}${user.role === "disabled" || user.role === "super_admin" ? "" : confirmationMarkup(user.id, "disable")}${user.role === "super_admin" ? "" : confirmationMarkup(user.id, "delete")}<p class="row-status">${escapeHtml(rowMessages.get(user.id) || "")}</p></td>
        </tr>
      `).join("")}</tbody>
    </table></div>
  `;
}

function usersListMarkup(message: string): string {
  return `<section class="panel workspace-panel"><div class="section-title"><h2>${adminCopy.usersHeading}</h2><button id="refreshUsers" type="button" class="secondary">${adminCopy.refresh}</button></div><div id="usersTable" class="table-placeholder">${loaderMarkup("thinking", adminCopy.loadUsersFailure)}</div><p class="status">${escapeHtml(message)}</p></section>`;
}

function createUserMarkup(currentUser: CurrentUser | null, credential: SessionState["setupCredential"], message: string): string {
  return `<section class="panel workspace-panel"><h2>${adminCopy.createUserHeading}</h2><form id="createUserForm" class="stack"><label>${adminCopy.username}<input name="username" required /></label><label>${adminCopy.role}<select name="role"><option value="user">${adminCopy.userRole}</option>${currentUser?.role === "super_admin" ? `<option value="admin">${adminCopy.adminRole}</option>` : ""}</select></label><button type="submit">${adminCopy.create}</button></form>${credential ? credentialMarkup(credential, { heading: appContent.credentials.temporaryCredentialReady, note: appContent.credentials.adminShareNote }) : ""}<p class="status">${escapeHtml(message)}</p></section>`;
}

function tuningTableMarkup(rules: TuningRule[], general: boolean): string {
  const matching = rules.filter((rule) => general
    ? rule.improvementType === "alias" && rule.status === "approved"
    : rule.improvementType !== "alias" || rule.status !== "approved");
  return matching.map((rule) => `
    <tr><td>${escapeHtml(rule.phrase)}</td><td>${escapeHtml(general ? rule.replacementOrExpansion || "—" : `${rule.targetClauseNumber || "—"}${rule.improvementType === "preferred_result" ? ` (${adminCopy.pinned})` : ""}`)}</td><td>${escapeHtml(rule.usageCount ? adminCopy.usedTimes.replace("{count}", String(rule.usageCount)) : adminCopy.notUsedYet)}</td><td>${escapeHtml(`${rule.improvementType} · ${rule.status}`)}</td><td>${rule.status === "approved" && ["alias", "clause_keyword", "preferred_result"].includes(rule.improvementType) ? `<button type="button" class="ghost danger deleteTuningButton" data-id="${escapeHtml(rule.id)}">${adminCopy.deleteRule}</button><div class="inline-confirm" data-tuning-id="${escapeHtml(rule.id)}" hidden><span>${adminCopy.confirmDeleteRule}</span><button type="button" class="ghost danger confirmTuningDelete" data-id="${escapeHtml(rule.id)}">${adminCopy.confirmDeleteRuleAction}</button><button type="button" class="ghost cancelTuningDelete" data-id="${escapeHtml(rule.id)}">${adminCopy.table.cancel}</button></div>` : "—"}</td></tr>
  `).join("");
}

function generalTermsMarkup(rules: TuningRule[]): string {
  return `<form id="generalTuningForm" class="stack tuning-form"><label>${adminCopy.phrase}<input name="phrase" required /></label><label>${adminCopy.expansion}<input name="expansion" required /></label><button type="submit">${adminCopy.addGeneral}</button></form><div class="users-table-scroll"><table class="users-table"><thead><tr><th>${adminCopy.phrase}</th><th>${adminCopy.expansion}</th><th>${adminCopy.usage}</th><th>${adminCopy.type}</th><th>${adminCopy.table.actions}</th></tr></thead><tbody>${tuningTableMarkup(rules, true)}</tbody></table></div>`;
}

function clauseTermsMarkup(rules: TuningRule[]): string {
  return `<form id="clauseTuningForm" class="stack tuning-form"><label>${adminCopy.phrase}<input name="phrase" required /></label><label>${adminCopy.clauseNumber}<input name="clauseNumber" required /></label><label><input name="pin" type="checkbox" /> ${adminCopy.pin}</label><button type="submit">${adminCopy.addClause}</button></form><div class="users-table-scroll"><table class="users-table"><thead><tr><th>${adminCopy.phrase}</th><th>${adminCopy.clauseNumber}</th><th>${adminCopy.usage}</th><th>${adminCopy.type}</th><th>${adminCopy.table.actions}</th></tr></thead><tbody>${tuningTableMarkup(rules, false)}</tbody></table></div>`;
}

function zeroResultsMarkup(items: ZeroResultQuery[]): string {
  return items.length
    ? `<ul class="zero-results">${items.map((item) => `<li><strong>${escapeHtml(item.query)}</strong><span>${escapeHtml(String(item.count))} · ${escapeHtml(item.lastSearchedAt)}</span></li>`).join("")}</ul>`
    : `<p class="muted">${adminCopy.noZeroResults}</p>`;
}

function bindTuningActions(options: AdminScreenOptions, reload: () => Promise<void>): void {
  const bindForm = (selector: string, build: (form: FormData) => Record<string, unknown>) => {
    document.querySelector<HTMLFormElement>(selector)?.addEventListener("submit", async (event) => {
      event.preventDefault();
      try {
        await options.createTuning(build(new FormData(event.currentTarget as HTMLFormElement)));
        await reload();
      } catch (error) {
        options.onMessage(error instanceof Error ? error.message : adminCopy.createTuningFailure);
        options.reloadAdmin();
      }
    });
  };
  bindForm("#generalTuningForm", (form) => ({ tier: "general", phrase: String(form.get("phrase") || ""), expansion: String(form.get("expansion") || "") }));
  bindForm("#clauseTuningForm", (form) => ({ tier: "clause", phrase: String(form.get("phrase") || ""), clauseNumber: String(form.get("clauseNumber") || ""), pin: form.get("pin") === "on" }));
  document.querySelectorAll<HTMLButtonElement>(".deleteTuningButton").forEach((button) => button.addEventListener("click", () => {
    button.hidden = true;
    document.querySelector<HTMLElement>(`.inline-confirm[data-tuning-id="${button.dataset.id}"]`)?.removeAttribute("hidden");
  }));
  document.querySelectorAll<HTMLButtonElement>(".cancelTuningDelete").forEach((button) => button.addEventListener("click", () => {
    document.querySelector<HTMLElement>(`.inline-confirm[data-tuning-id="${button.dataset.id}"]`)?.setAttribute("hidden", "");
    const trigger = document.querySelector<HTMLButtonElement>(`.deleteTuningButton[data-id="${button.dataset.id}"]`);
    if (trigger) trigger.hidden = false;
  }));
  document.querySelectorAll<HTMLButtonElement>(".confirmTuningDelete").forEach((button) => button.addEventListener("click", async () => {
    try {
      await options.deleteTuning(button.dataset.id || "");
      await reload();
    } catch (error) {
      options.onMessage(error instanceof Error ? error.message : adminCopy.deleteTuningFailure);
      options.reloadAdmin();
    }
  }));
}

function bindUserActions(options: AdminScreenOptions, reload: () => Promise<void>, rowMessages: Map<string, string>): void {
  document.querySelectorAll<HTMLButtonElement>(".resetUserButton, .disableUserButton, .deleteUserButton").forEach((button) => button.addEventListener("click", () => {
    const action = button.classList.contains("resetUserButton") ? "reset" : button.classList.contains("disableUserButton") ? "disable" : "delete";
    button.hidden = true;
    document.querySelector<HTMLElement>(`.inline-confirm[data-user-id="${button.dataset.userId}"][data-confirm-action="${action}"]`)?.removeAttribute("hidden");
  }));
  document.querySelectorAll<HTMLButtonElement>(".cancelUserActionButton").forEach((button) => button.addEventListener("click", () => {
    const action = button.dataset.confirmAction || "";
    document.querySelector<HTMLElement>(`.inline-confirm[data-user-id="${button.dataset.userId}"][data-confirm-action="${action}"]`)?.setAttribute("hidden", "");
    const trigger = document.querySelector<HTMLButtonElement>(`.${action}UserButton[data-user-id="${button.dataset.userId}"]`);
    if (trigger) trigger.hidden = false;
  }));
  document.querySelectorAll<HTMLButtonElement>(".confirmUserActionButton").forEach((button) => button.addEventListener("click", async () => {
    const action = button.dataset.confirmAction;
    const userId = button.dataset.userId || "";
    try {
      if (action === "reset") {
        options.onCredential(await options.resetPassword(userId));
        options.reloadAdmin();
      } else if (action === "disable") {
        await options.disableUser(userId);
        options.onUserDisabled(document.querySelector<HTMLButtonElement>(`.disableUserButton[data-user-id="${userId}"]`)?.dataset.username || "user");
        await reload();
      } else if (action === "delete") {
        await options.deleteUser(userId);
        rowMessages.delete(userId);
        await reload();
      }
    } catch (error) {
      rowMessages.set(userId, error instanceof Error ? error.message : adminCopy.deleteUserFailure);
      await reload();
    }
  }));
  document.querySelectorAll<HTMLButtonElement>(".licensedAccessButton").forEach((button) => button.addEventListener("click", async () => {
    try {
      await options.setLicensedAccess(button.dataset.userId || "", button.dataset.enabled !== "true");
      await reload();
    } catch (error) {
      options.onMessage(error instanceof Error ? error.message : adminCopy.licensedAccessFailure);
      options.reloadAdmin();
    }
  }));
  document.querySelectorAll<HTMLButtonElement>(".enableUserButton").forEach((button) => button.addEventListener("click", async () => {
    try {
      await options.enableUser(button.dataset.userId || "");
      await reload();
    } catch (error) {
      options.onMessage(error instanceof Error ? error.message : adminCopy.enableUserFailure);
      options.reloadAdmin();
    }
  }));
  options.bindCopyButtons();
}

export function renderAdminScreen(options: AdminScreenOptions): void {
  const isAdmin = options.currentUser?.role === "admin" || options.currentUser?.role === "super_admin";
  const tabs = isAdmin ? adminConsoleTabs : settingsConsoleTabs;
  let selectedTop: ConsoleTab | SettingsTab = isAdmin ? "users" : "password";
  let selectedUsers: UsersTab = options.setupCredential ? "create" : "list";
  let selectedTerms: TermsTab = "general";
  let selectedReads: ReadsTab = "create";
  let selectedReports: ReportsTab = "all";
  let reportsPage = 1;
  let selectedSettings: SettingsTab = "password";
  let settingsMessage = "";
  const rowMessages = new Map<string, string>();

  const downloadAction = isAdmin ? `<button id="downloadStats" type="button" class="ghost console-trailing-action">${adminCopy.downloadStats}</button>` : "";
  options.renderWorkspace(consoleShellMarkup(tabs, selectedTop, downloadAction));
  options.bindWorkspaceNavigation();
  document.querySelector<HTMLButtonElement>("#downloadStats")?.addEventListener("click", async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    try {
      const { blob, filename } = await options.downloadStats();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      options.onMessage(error instanceof Error ? error.message : adminCopy.downloadStatsFailure);
      options.reloadAdmin();
    } finally {
      button.disabled = false;
    }
  });

  const panel = (): HTMLDivElement | null => document.querySelector<HTMLDivElement>("#consolePanel");
  const setPanel = (markup: string): HTMLDivElement | null => {
    const target = panel();
    if (target) target.innerHTML = markup;
    return target;
  };

  const loadUsers = async () => {
    const target = document.querySelector<HTMLDivElement>("#usersTable");
    if (!target) return;
    try {
      const payload = await options.listUsers();
      target.innerHTML = usersTableMarkup(payload.users, rowMessages);
      bindUserActions(options, loadUsers, rowMessages);
    } catch (error) {
      target.textContent = error instanceof Error ? error.message : adminCopy.loadUsersFailure;
    }
  };

  const showUsers = () => {
    const content = selectedUsers === "list"
      ? usersListMarkup(options.message)
      : createUserMarkup(options.currentUser, options.setupCredential, options.message);
    setPanel(`${subTabMarkup([{ id: "list", label: adminCopy.usersListTab }, { id: "create", label: adminCopy.usersCreateTab }], selectedUsers)}${panelMarkup(adminCopy.explainers[selectedUsers === "list" ? "users" : "createUser"], content)}`);
    bindSubTabs((tab) => {
      selectedUsers = tab as UsersTab;
      showUsers();
    });
    if (selectedUsers === "list") {
      document.querySelector<HTMLButtonElement>("#refreshUsers")?.addEventListener("click", loadUsers);
      void loadUsers();
    } else {
      document.querySelector<HTMLFormElement>("#createUserForm")?.addEventListener("submit", async (event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget as HTMLFormElement);
        try {
          options.onCredential(await options.createUser(String(form.get("username") || ""), String(form.get("role") || "user")));
          options.reloadAdmin();
        } catch (error) {
          options.onMessage(error instanceof Error ? error.message : adminCopy.createUserFailure);
          options.reloadAdmin();
        }
      });
      options.bindCopyButtons();
    }
  };

  const showTerms = async () => {
    const target = setPanel(`${subTabMarkup([{ id: "general", label: adminCopy.generalTerms }, { id: "clause", label: adminCopy.clauseTerms }, { id: "zeroes", label: adminCopy.zeroResults }], selectedTerms)}<div id="termsContent">${loaderMarkup("thinking", adminCopy.loadTuningFailure)}</div>`);
    if (!target) return;
    bindSubTabs((tab) => {
      selectedTerms = tab as TermsTab;
      void showTerms();
    });
    try {
      const payload = await options.listTuning();
      const content = selectedTerms === "general" ? generalTermsMarkup(payload.rules) : selectedTerms === "clause" ? clauseTermsMarkup(payload.rules) : zeroResultsMarkup(payload.zeroResultQueries);
      const termsContent = document.querySelector<HTMLDivElement>("#termsContent");
      if (termsContent) termsContent.innerHTML = panelMarkup(adminCopy.explainers[selectedTerms === "zeroes" ? "zeroResults" : selectedTerms === "general" ? "generalTerms" : "clauseTerms"], content);
      bindTuningActions(options, showTerms);
    } catch (error) {
      const termsContent = document.querySelector<HTMLDivElement>("#termsContent");
      if (termsContent) termsContent.textContent = error instanceof Error ? error.message : adminCopy.loadTuningFailure;
    }
  };

  const readsSubTabs = () => [
    { id: "create", label: adminCopy.createReadHeading },
    { id: "queue", label: adminCopy.reviewQueue },
    { id: "needs", label: adminCopy.needsRead }
  ];

  const showReads = async (prefillClause = "") => {
    const target = setPanel(`${subTabMarkup(readsSubTabs(), selectedReads)}<div id="readsContent">${loaderMarkup("thinking", adminCopy.loadReadsFailure)}</div>`);
    if (!target) return;
    bindSubTabs((tab) => {
      selectedReads = tab as ReadsTab;
      void showReads();
    });
    try {
      const payload = await options.listReads();
      const content = selectedReads === "create" ? createReadMarkup() : selectedReads === "queue" ? reviewQueueMarkup(payload.reads) : needsReadMarkup(payload.suggestions);
      setPanel(`${subTabMarkup(readsSubTabs(), selectedReads, { queue: draftReadCount(payload.reads) })}<div id="readsContent">${panelMarkup(adminCopy.explainers[selectedReads === "create" ? "createRead" : selectedReads === "queue" ? "reviewQueue" : "needsRead"], content)}</div>`);
      bindSubTabs((tab) => {
        selectedReads = tab as ReadsTab;
        void showReads();
      });
      if (prefillClause) {
        const input = document.querySelector<HTMLInputElement>("#createReadForm input[name='clauseNumber']");
        if (input) input.value = prefillClause;
      }
      bindReadsSection({
        createRead: options.createRead,
        updateRead: options.updateRead,
        approveRead: options.approveRead,
        deleteRead: options.deleteRead,
        reload: showReads,
        onError: (message) => {
          options.onMessage(message);
          options.reloadAdmin();
        },
        onUseSuggestion: (clauseNumber) => {
          selectedReads = "create";
          void showReads(clauseNumber);
        }
      });
    } catch (error) {
      const readsContent = document.querySelector<HTMLDivElement>("#readsContent");
      if (readsContent) readsContent.textContent = error instanceof Error ? error.message : adminCopy.loadReadsFailure;
    }
  };

  const reportsSubTabs = () => [
    { id: "all", label: adminCopy.reportsAll },
    { id: "new", label: adminCopy.reportsNew },
    { id: "triaged", label: adminCopy.reportsTriaged },
    { id: "fixed", label: adminCopy.reportsFixed },
    { id: "wontfix", label: adminCopy.reportsWontfix }
  ];

  // Filter and page changes fire in quick succession, and an older response
  // arriving late would repaint the panel under the wrong filter. Only the
  // newest request is allowed to render.
  let reportsVersion = 0;

  const showReports = async (page = reportsPage) => {
    if (selectedTop !== "reports") return;
    const version = ++reportsVersion;
    reportsPage = page;
    const target = setPanel(`${subTabMarkup(reportsSubTabs(), selectedReports)}<div id="reportsContent">${loaderMarkup("thinking", adminCopy.loadReportsFailure)}</div>`);
    if (!target) return;
    bindSubTabs((tab) => {
      selectedReports = tab as ReportsTab;
      reportsPage = 1;
      void showReports(1);
    });
    try {
      const payload = await options.listReports(selectedReports === "all" ? null : selectedReports, reportsPage);
      if (version !== reportsVersion || selectedTop !== "reports") return;
      if (payload.page > payload.totalPages) {
        reportsPage = payload.totalPages;
        await showReports(payload.totalPages);
        return;
      }
      setPanel(`${subTabMarkup(reportsSubTabs(), selectedReports, { new: payload.newCount })}<div id="reportsContent">${panelMarkup(adminCopy.explainers.reports, reportsMarkup(payload))}</div>`);
      bindSubTabs((tab) => {
        selectedReports = tab as ReportsTab;
        reportsPage = 1;
        void showReports(1);
      });
      bindReportsSection({
        updateReportStatus: options.updateReportStatus,
        reload: showReports,
        onError: (message) => options.onMessage(message)
      });
    } catch (error) {
      if (version !== reportsVersion || selectedTop !== "reports") return;
      const reportsContent = document.querySelector<HTMLDivElement>("#reportsContent");
      if (reportsContent) reportsContent.textContent = error instanceof Error ? error.message : adminCopy.loadReportsFailure;
    }
  };

  const showSettings = () => {
    const activeSettings = isAdmin ? selectedSettings : (selectedTop as SettingsTab);
    const content = activeSettings === "password" ? changePasswordMarkup(settingsMessage) : changeEmailMarkup(settingsMessage);
    setPanel(isAdmin
      ? `${subTabMarkup(settingsConsoleTabs, selectedSettings)}${panelMarkup(adminCopy.explainers[selectedSettings], content)}`
      : panelMarkup(adminCopy.explainers[activeSettings], content));
    if (isAdmin) bindSubTabs((tab) => {
      selectedSettings = tab as SettingsTab;
      settingsMessage = "";
      showSettings();
    });
    bindSettingsForm({
      message: settingsMessage,
      changePassword: options.changePassword,
      changeEmail: options.changeEmail,
      onMessage: (message) => {
        settingsMessage = message;
      },
      reload: showSettings
    });
  };

  const showTop = (tab: ConsoleTab | SettingsTab) => {
    selectedTop = tab;
    document.querySelectorAll<HTMLButtonElement>("[data-console-tab]").forEach((button) => {
      const active = button.dataset.consoleTab === tab;
      button.classList.toggle("active", active);
      button.setAttribute("aria-selected", String(active));
    });
    if (tab === "users") showUsers();
    else if (tab === "terms") void showTerms();
    else if (tab === "reads") void showReads();
    else if (tab === "reports") void showReports();
    else {
      if (!isAdmin) selectedSettings = tab as SettingsTab;
      showSettings();
    }
  };

  bindConsoleTabs((tab) => showTop(tab as ConsoleTab | SettingsTab));
  showTop(selectedTop);
}
